import { holdsValue, isEmptyValue, mergeFileValues, planAutofill, type AutofillOffer, type FileValues } from './merge'
import type { AutofillValue } from './targets'

/** A value a document put in a field, and the card it came from; `card` is null once HR accepted it with "Use" (it is HR's then). */
export interface FilledEntry {
    value: AutofillValue
    card: string | null
}

/** The values a card offers for a field: one when its files agree, several when they disagree. */
export interface OfferedEntry {
    offers: AutofillOffer[]
    card: string
}

/** What the documents did to the form: the values they filled, and the ones they offer beside a field. */
export interface AutofillMemory<K extends string> {
    filled: Partial<Record<K, FilledEntry>>
    offered: Partial<Record<K, OfferedEntry>>
}

/** What a card's read did, for its status line. */
export interface CardSummary {
    /** Fields the files gave a value for. */
    found: number
    filled: number
    /** Fields HR filled differently: the value is offered. */
    differs: number
    /** Fields the files disagree on: every value is offered, none written. */
    conflicts: number
    /** Fields the form does not let a document change. */
    locked: number
}

interface CardPlanInput<K extends string> {
    card: string
    /** Every file on the card that was read, with what it gives. None: the card's values are taken back. */
    files: ReadonlyArray<FileValues<K>>
    current: (key: K) => unknown
    memory: AutofillMemory<K>
    locked: ReadonlySet<K>
    /** Fields the form does not show now. What one holds is never replaced or emptied, since HR can't see it: an empty one is filled, a value only offered. */
    hidden?: ReadonlySet<K>
}

interface CardPlan<K extends string> {
    /** One write for the form: values to fill, and `null` for a field the card filled that its files no longer give. */
    write: Partial<Record<K, AutofillValue | null>>
    memory: AutofillMemory<K>
    summary: CardSummary
}

/**
 * What a card's files do to the form, planned again whenever they change (read, a file removed): the values the files agree on fill
 * by `planAutofill`; a field they disagree on is never written and offers every value; a field the card filled earlier that its files
 * no longer give is emptied while HR has not touched it. A value HR accepted is HR's and stays, and so is a field HR emptied after a
 * document filled it: it is never filled again, its value is only offered. A field the form hides is never replaced or emptied, since
 * HR can't see it: an empty one is filled, and a value it holds is only offered.
 */
export function planCard<K extends string>({ card, files, current, memory, locked, hidden = new Set() }: CardPlanInput<K>): CardPlan<K> {
    const { agreed, conflicts } = mergeFileValues(files)
    const write: Partial<Record<K, AutofillValue | null>> = {}
    for (const key in memory.filled) {
        const entry = memory.filled[key]
        if (entry?.card === card && !hidden.has(key) && agreed[key] === undefined && holdsValue(current(key), entry.value)) write[key] = null
    }
    // What the field holds once the emptied values are gone.
    const now = (key: K): unknown => (write[key] === null ? undefined : current(key))
    // Values a document put in and HR has not taken over: what this card may replace.
    const replaceable: Partial<Record<K, AutofillValue>> = {}
    for (const key in memory.filled) {
        const entry = memory.filled[key]
        if (entry && entry.card !== null && !hidden.has(key)) replaceable[key] = entry.value
    }
    // A field a document filled that HR has emptied since: clearing it is HR's change, so a re-plan (Cancel, a file removed, another
    // read) offers the files' value beside it instead of writing it back. HR can't empty a field they can't see: an empty hidden field
    // is filled, and a locked one is kept.
    const cleared = new Set<K>()
    for (const key in memory.filled) if (!locked.has(key) && !hidden.has(key) && isEmptyValue(current(key))) cleared.add(key)
    const toFill: Partial<Record<K, AutofillValue>> = {}
    for (const key in agreed) if (!cleared.has(key)) toFill[key] = agreed[key]
    const plan = planAutofill(toFill, now, replaceable, locked)
    for (const key of cleared) {
        const value = agreed[key]
        if (value !== undefined && !locked.has(key)) plan.differs[key] = value
    }
    Object.assign(write, plan.fill)

    const filled: Partial<Record<K, FilledEntry>> = {}
    for (const key in memory.filled) {
        const entry = memory.filled[key]
        if (entry && entry.card !== card && plan.fill[key] === undefined && !cleared.has(key)) filled[key] = entry
    }
    for (const key in agreed) {
        const value = agreed[key]
        if (value === undefined) continue
        const own = memory.filled[key]?.card === card && holdsValue(now(key), value)
        if (plan.fill[key] !== undefined || own) filled[key] = { value, card }
    }

    const offered: Partial<Record<K, OfferedEntry>> = {}
    // A field this card filled drops any older offer for it: the value now in it came from a document.
    for (const key in memory.offered) {
        const entry = memory.offered[key]
        if (entry && entry.card !== card && plan.fill[key] === undefined) offered[key] = entry
    }
    for (const key in plan.differs) {
        const value = plan.differs[key]
        // An agreed value: every file that gives the field gives this value.
        const fileNames = files.filter((file) => file.values[key] !== undefined).map((file) => file.fileName)
        if (value !== undefined) offered[key] = { offers: [{ value, fileNames }], card }
    }
    let conflictCount = 0
    for (const key in conflicts) {
        const offers = conflicts[key]
        if (!offers || locked.has(key)) continue
        offered[key] = { offers, card }
        conflictCount++
    }

    const found = new Set<string>([...Object.keys(agreed), ...Object.keys(conflicts)])
    return {
        write,
        memory: { filled, offered },
        summary: {
            found: found.size,
            filled: Object.keys(plan.fill).length,
            differs: Object.keys(plan.differs).length,
            conflicts: conflictCount,
            locked: [...found].filter((key) => locked.has(key as K)).length,
        },
    }
}
